import { isAgronomist } from "@omnia/shared/session/utils"
import { camelCaseKeys } from "../../case"
import { HoldingSatelliteOptions } from "../../holding"
import { RoleId } from "../../user"
import { UserPermissions } from "../types"
import { ApiUserPermissions } from "./api"
import { getClimatePermissions } from "./climate"
import { getDefaultPermissions } from "./default"

export function getUserPermissions(p: ApiUserPermissions, role: RoleId): UserPermissions {

    if (!p) {
        return getDefaultPermissions(role)
    }

    const access = role === RoleId.Access
    const admin = role <= RoleId.Admin
    const serviceSpecialist = role === RoleId.ServiceSpecialist

    const precision = getPrecisionPermission(p, role)
    const mapLayers = access || (!!p.MapLayersModule)
    const reports = p.Reports
    const fieldDiary = !!(p.FieldDiary ?? admin)

    const exports = getExportPermissions(p, role)

    const result: UserPermissions = {

        holdings: readWrite(p.ManageHoldings),

        currentHolding: { write: p.ManageCurrentHolding },

        fields: readWrite(p.Fields),

        soil: readWrite(p.Soil),
        soilManagementPlan: !!(p.Soil && p.SoilManagementPlan),

        stocks: { ...readWrite(p.Stocks), financialData: true },

        mapLayers: {
            ...readWrite(mapLayers),
            canEditVariants: admin
        },

        seedPlans: readWrite(p.SeedPlans),

        nutritionPlans: ({
            ...readWrite(p.NutritionPlans),
            calculateAllPlans: admin
        }),

        sprayPlans: getSprayPlanPermissions(p),

        connect: !!p.OmniaConnectFeature,

        manure: readWrite(!!p.ManureManagementFeature),

        precision,

        yield: readWrite(p.Yield),
        shed: { ...readWrite(p.Shed), financialData: true },
        production: readWrite(p.Production),
        planning: readWrite(p.Planning),

        imports: {
            enabled: !!p.ImportModule,
            layers: (!!p.MapLayersModule) || precision,
            holding: admin,
            terramap: admin,
            soilOptixAdmin: Boolean(p.Admin?.SoilOptix),
            glgm: !access
        },

        exports,

        reports: {
            enabled: reports
        },

        climate: getClimatePermissions(role, p.Climate),

        fieldDiary: {
            ...readWrite(fieldDiary),
            editAnyEntry: admin
        },

        taskManagement: readWrite(!!p.TaskManagement),

        asApplied: {
            viewDetails: admin
        },

        satellite: {
            viewClouds: admin || serviceSpecialist
        },

        admin: camelCaseKeys(p.Admin)

    }

    return result

}

function getSprayPlanPermissions(p: ApiUserPermissions): UserPermissions["sprayPlans"] {

    const sprayPlans = p.SprayPlans

    if (!sprayPlans) {
        return {
            ...readWrite(false),
            auditPlans: {
                audit: true
            }
        }
    }

    const { managePlans, manageSharedTemplates, ifsOrders, auditPlans } = camelCaseKeys(sprayPlans)

    return {
        ...readWrite(managePlans),
        auditPlans,
        manageSharedTemplates,
        ifsOrders
    }

}

function getExportPermissions(p: ApiUserPermissions, role: RoleId): UserPermissions["exports"] {

    const access = role == RoleId.Access
    const agronomist = isAgronomist(role)
    const serviceSpecialist = role === RoleId.ServiceSpecialist

    const exportOptions = p.ExportOptions ?? {
        Holdings: agronomist,
        Boundaries: true,
        Layers: true,
        Plans: true
    }

    const module = !!p.ExportModule

    const glgm = !!(p.GlgmExports) && (!access)

    const enabled =
        Object.values(exportOptions).some(o => o)
        || glgm
        || p.OmniaConnectFeature

    return {

        enabled: module && enabled,
        glgm,

        holding: exportOptions.Holdings,

        claasMachines: !!(module && p.ExportToMachines?.CLAAS),

        boundaries: exportOptions.Boundaries
            ? {
                terramap: agronomist || serviceSpecialist
            }
            : undefined,

        layers: exportOptions.Layers,
        plans: exportOptions?.Plans

    }

}
export function setupSatelliteOptions(sat: HoldingSatelliteOptions) {

    const enabled = sat?.enabled && sat?.indices?.ndvi

    sat = {
        enabled,
        indices: {
            ndvi: false
        },
        ...sat
    }

    return sat

}


/**
 * Replaces the previous "PermissionFeature" permission, at least temporarily
 * @param p Permissions
 * @param role Role
 */
function getPrecisionPermission(p: ApiUserPermissions, role: RoleId) {

    if (role <= RoleId.Agronomist) {
        return true
    }

    switch (role) {

        case RoleId.FmClient:
            return false

        default:
            return !!p?.MapLayersModule

    }

}



function readWrite(on: boolean) {
    return { read: !!on, write: !!on }
}
